import classNames from 'clsx'
import { arrayOf, func, number, shape, string } from 'prop-types'

import { withI18n } from 'shared/utils/withI18n'
import ErrorMessage from 'views/components/atoms/ErrorMessage'
import Select from 'views/components/molecules/Select'
import { i18nType } from 'views/utils/propTypes'

import { getDays, getMonths, getYears } from './helpers'

import './BirthDaySelect.scss'

const currentYear = new Date().getFullYear()
const startYear = currentYear - 120
const endYear = currentYear

const BirthDaySelect = ({
  minAge,
  daySelectName,
  monthSelectName,
  yearSelectName,
  selectedDay,
  onSelectedDayChange = value => value,
  selectedMonth,
  onSelectedMonthChange = value => value,
  selectedYear,
  onSelectedYearChange = value => value,
  errorMessage,
  i18n,
  range = {
    startYear,
    endYear,
  },
  enabledYears = [],
  dataQaDay = '',
  dataQaMonth = '',
  dataQaYear = '',
  type,
}) => {
  const typePrefix = type ? `${type}-` : ''
  const className = classNames('o-BirthDaySelect', {
    'has-error': errorMessage,
  })

  const props = {
    className,
    'data-clientside-hook': 'formBirthdaySelectValidation',
    'aria-label': i18n._('registration.birthDate.selection'),
    'data-day-error-msg': i18n._(
      'login.createAccount.dateSelection.outOfRange'
    ),
  }

  if (minAge) {
    props['data-min-age'] = minAge
    props['data-min-age-error-msg'] = i18n._(
      'login.createAccount.dateSelection.minAge',
      { minAge }
    )
  }

  return (
    <div {...props}>
      <div className="l-flex l-flex--center@small-tiny a-list-reset o-BirthDaySelect__select-wrapper">
        <div className="l-flex o-BirthDaySelect__day-and-month-container">
          <div
            className="u-margin-xx-small--right u-margin--right@tiny l-flex__grow-one@small-tiny o-BirthDaySelect__select-container"
            data-clientside-hook="birthDaySelect__day"
            aria-label={i18n._('registration.birthDate.selection.day')}
          >
            <Select
              id={`${typePrefix}${daySelectName}`}
              name={daySelectName}
              options={getDays(i18n._('registration.birthDate.DD'))}
              label=""
              dataQa={dataQaDay}
              showLabel={false}
              selectedValue={selectedDay}
              value={selectedDay ? selectedDay : ''}
              onChange={event => {
                onSelectedDayChange(parseInt(event.target.value))
              }}
              required
            />
          </div>
          <div
            className="u-margin-xx-small--right u-margin--right@tiny l-flex__grow-one@small-tiny o-BirthDaySelect__select-container o-BirthDaySelect__select-month"
            data-clientside-hook="birthDaySelect__month"
            aria-label={i18n._('registration.birthDate.selection.month')}
          >
            <Select
              id={`${typePrefix}${monthSelectName}`}
              name={monthSelectName}
              options={getMonths(i18n._('registration.birthDate.MM'))}
              label=""
              dataQa={dataQaMonth}
              showLabel={false}
              selectedValue={selectedMonth}
              value={selectedMonth ? selectedMonth : ''}
              onChange={event =>
                onSelectedMonthChange(parseInt(event.target.value))
              }
              required
            />
          </div>
        </div>
        <div
          className="l-flex__grow-one@small-tiny o-BirthDaySelect__select-container o-BirthDaySelect__select-year"
          data-clientside-hook="birthDaySelect__year"
          aria-label={i18n._('registration.birthDate.selection.year')}
        >
          <Select
            id={`${typePrefix}${yearSelectName}`}
            name={yearSelectName}
            options={getYears(minAge, i18n._('registration.birthDate.YYYY'), {
              range,
              enabledYears,
            })}
            label=""
            dataQa={dataQaYear}
            showLabel={false}
            selectedValue={selectedYear}
            value={selectedYear ? selectedYear : ''}
            onChange={event =>
              onSelectedYearChange(parseInt(event.target.value))
            }
            required
          />
        </div>
      </div>
      <ErrorMessage text={errorMessage} />
    </div>
  )
}

BirthDaySelect.propTypes = {
  type: string,
  i18n: i18nType.isRequired,
  daySelectName: string.isRequired,
  monthSelectName: string.isRequired,
  yearSelectName: string.isRequired,
  minAge: number,
  selectedDay: number,
  onSelectedDayChange: func,
  selectedMonth: number,
  onSelectedMonthChange: func,
  selectedYear: number,
  onSelectedYearChange: func,
  errorMessage: string,
  enabledYears: arrayOf(number),
  range: shape({ endYear: number, startYear: number }),
  dataQaDay: string,
  dataQaMonth: string,
  dataQaYear: string,
}

BirthDaySelect.defaultProps = {
  minAge: null,
  selectedDay: null,
  selectedYear: null,
  selectedMonth: null,
  onSelectedDayChange: () => {},
  onSelectedMonthChange: () => {},
  onSelectedYearChange: () => {},
  errorMessage: null,
  enabledYears: [],
  range: {
    startYear,
    endYear,
  },
  dataQaDay: '',
  dataQaMonth: '',
  dataQaYear: '',
}

export default withI18n()(BirthDaySelect)
